// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { Sans16, spacing, typography } from "@recidiviz/design-system";
import { uniqBy } from "lodash";
import { observer } from "mobx-react-lite";
import { rem } from "polished";
import { useState } from "react";
import styled from "styled-components";

import { Icon, palette } from "~design-system";

import StopwatchIcon from "../../assets/static/images/stopwatch.svg?react";
import { useFeatureVariants } from "../../components/StoreProvider";
import useIsMobile from "../../hooks/useIsMobile";
import { formatWorkflowsDate } from "../../utils";
import { SupervisionTask } from "../../WorkflowsStore";
import { type SnoozeOptions } from "../../WorkflowsStore/Task/types";
import { formatDateString } from "../models/utils";
import { InfoTooltipWrapper } from "../WorkflowsJusticeInvolvedPersonProfile/styles";
import { PersonProfileProps } from "../WorkflowsJusticeInvolvedPersonProfile/types";
import { NEED_DISPLAY_NAME } from "./fixtures";
import { SnoozeTaskDropdown } from "./SnoozeTaskDropdown";
import { snoozeTaskToast } from "./snoozeTaskToast";
import { TaskSnoozeReasonForm } from "./TaskSnoozeReasonForm";

const TasksWrapper = styled.div``;
const TaskItems = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const TaskItem = styled(Sans16)<{ showSnoozeDropdown?: boolean }>`
  padding: 1.5rem 0;
  display: grid;
  grid-template-columns: 5fr ${({ showSnoozeDropdown }) =>
      showSnoozeDropdown ? "1fr" : "auto"};
  align-content: center;
  position: relative;

  &:last-of-type {
    padding-bottom: 1.5rem;
  }

  /* Replaces TaskItemDivider - creates dividers between tasks using CSS pseudo-selectors */
  &:not(:last-of-type)::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background-color: ${palette.slate20};
  }
`;

const TaskName = styled(Sans16)`
  color: ${palette.pine1};
  min-width: fit-content;
`;

const TaskItemWrapper = styled.div`
  padding: 1.5rem 0;
  position: relative;

  &:last-of-type {
    padding-bottom: 1.5rem;
  }

  &:not(:last-of-type)::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background-color: ${palette.slate20};
  }
`;

const TaskItemV2 = styled(Sans16)<{ showSnoozeDropdown?: boolean }>`
  display: grid;
  grid-template-columns: 3fr 4fr 1fr;
`;

const TaskInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const TaskFrequency = styled.div`
  font-size: ${rem(14)};
  margin-top: ${rem(6)};
`;

const TaskTimelineGroup = styled.div`
  display: grid;
  align-items: baseline;
  grid-template-rows: 1fr auto;
  grid-template-columns: ${rem(16)} auto;
  column-gap: ${rem(6)};
  row-gap: 0;
`;

const TaskTimelineIcon = styled.div`
  height: 100%;
  overflow: hidden;
`;

const TaskTimelineLineWrapper = styled.div`
  width: 100%;
  height: 100%;
  margin-top: ${rem(spacing.xs)};

  display: flex;
  justify-content: center;
`;

const TaskTimelineLine = styled.div`
  width: ${rem(1)};
  height: 100%;
  background-color: ${palette.slate60};
`;

const TaskTimelineText = styled.div`
  font-size: ${rem(14)};

  &:not(:last-child) {
    padding-bottom: ${rem(16)};
  }
`;

const TaskTimelineDueDate = styled.div<{ overdue: boolean }>`
  color: ${({ overdue }) => (overdue ? palette.signal.error : palette.pine4)};
`;

const SnoozedTaskIcon = styled.span`
  color: ${palette.slate60};
`;

const SnoozedTaskText = styled.span`
  color: ${palette.pine1};
`;

const SnoozedTaskInfoBox = styled.div`
  ${typography.Sans14}
  background-color: ${palette.marble4};
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1.5rem;

  display: flex;
  flex-direction: row;
  gap: ${rem(10)};
`;

const SnoozedTaskReason = styled.div`
  display: block;
  margin-top: ${rem(4)};
  color: ${palette.slate85};
`;

const TaskTimelineDonut = ({
  filled = false,
  lineBelow = false,
}: {
  filled?: boolean;
  lineBelow?: boolean;
}) => {
  return (
    <TaskTimelineIcon>
      <svg viewBox="0 0 14 14" aria-hidden={true}>
        <circle cx="50%" cy="50%" r="48%" fill={palette.slate20} />
        <circle
          cx="50%"
          cy="50%"
          r="25%"
          fill={filled ? palette.slate60 : "white"}
        />
      </svg>
      {lineBelow && (
        <TaskTimelineLineWrapper>
          <TaskTimelineLine />
        </TaskTimelineLineWrapper>
      )}
    </TaskTimelineIcon>
  );
};

const Wrapper = styled.div`
  color: ${palette.slate50};
  font-weight: 500;
  font-size: ${rem(14)};
  letting-spacing: -1%;
  line-height: 120%;
`;

const ContactWindow = ({ task }: { task: SupervisionTask }) => {
  return <Wrapper>{task.contactWindow}</Wrapper>;
};

export const SnoozedTaskInfo = ({ task }: { task: SupervisionTask }) => {
  if (!task.isSnoozed || !task.snoozeInfo) return null;
  const { snoozedBy, snoozedUntil, snoozedOn, snoozeReason } = task.snoozeInfo;
  const formattedSnoozedOn = formatWorkflowsDate(formatDateString(snoozedOn));
  const snoozeText =
    snoozedUntil === "FOREVER"
      ? `This task is hidden permanently. Marked as hidden by ${snoozedBy} on ${formattedSnoozedOn}.`
      : `This task is hidden until ${formatWorkflowsDate(snoozedUntil)}. Marked as hidden by ${snoozedBy} on ${formattedSnoozedOn}.`;
  return (
    <SnoozedTaskInfoBox>
      <SnoozedTaskIcon>
        <StopwatchIcon />
      </SnoozedTaskIcon>
      <SnoozedTaskText>
        {snoozeText}
        {snoozeReason && (
          <SnoozedTaskReason>Reason: {snoozeReason}</SnoozedTaskReason>
        )}
      </SnoozedTaskText>
    </SnoozedTaskInfoBox>
  );
};

/**
 * Whether a snooze reason is mandatory for the given duration. Gated on the
 * tenant opting in via `snoozeReasonRequiredOverDays`: when set, snoozes longer
 * than that many days (and permanent "FOREVER" snoozes) require a reason; when
 * unset, the reason stays optional for every duration.
 */
export const isSnoozeReasonRequired = (
  days: SnoozeOptions,
  requiredOverDays: number | undefined,
): boolean => {
  if (requiredOverDays === undefined) return false;
  return days === "FOREVER" || days > requiredOverDays;
};

const TaskPreviewV2 = ({ task }: { task: SupervisionTask }) => {
  const { isMobile } = useIsMobile(true);
  const { taskSnoozeReason } = useFeatureVariants();
  const reasonEnabled = Boolean(taskSnoozeReason);
  const [pendingSnoozeDays, setPendingSnoozeDays] =
    useState<SnoozeOptions | null>(null);

  // V2 (US_TX) intentionally omits the operations-metrics line from the
  // toast — see snoozeTaskToast's operationsInfoInToast arg.
  const persistSnooze = (days: SnoozeOptions, reason: string | undefined) => {
    task.updateSupervisionTask(days, reason);
    snoozeTaskToast(task, days, false, isMobile);
  };

  if (reasonEnabled && pendingSnoozeDays !== null) {
    return (
      <TaskItemWrapper>
        <TaskSnoozeReasonForm
          required={isSnoozeReasonRequired(
            pendingSnoozeDays,
            task.person.supervisionTasks?.tasksConfig
              ?.snoozeReasonRequiredOverDays,
          )}
          onCancel={() => setPendingSnoozeDays(null)}
          onSave={(reason) => {
            persistSnooze(pendingSnoozeDays, reason);
            setPendingSnoozeDays(null);
          }}
        />
      </TaskItemWrapper>
    );
  }

  return (
    <TaskItemWrapper>
      <TaskItemV2>
        <TaskInfo>
          <TaskName>
            {task.displayName}
            {task.tooltipCopy && (
              <>
                {" "}
                <InfoTooltipWrapper contents={task.tooltipCopy} maxWidth={340}>
                  <Icon kind="Info" size={12} color={palette.slate60} />
                </InfoTooltipWrapper>
              </>
            )}
          </TaskName>
          <ContactWindow task={task} />
          <TaskFrequency>
            <i className="fa fa-refresh" /> {task.frequency}
          </TaskFrequency>
        </TaskInfo>

        <TaskTimelineGroup>
          <TaskTimelineDonut filled lineBelow />
          <TaskTimelineText>
            <div>{task.additionalDetails}</div>
          </TaskTimelineText>

          {task.supplementaryContacts.map((contact) => (
            <>
              <TaskTimelineDonut lineBelow filled />
              <TaskTimelineText>
                <div>{contact}</div>
              </TaskTimelineText>
            </>
          ))}

          {task.futureScheduledContacts &&
            task.futureScheduledContacts.length > 0 && (
              <>
                <TaskTimelineDonut lineBelow />
                <TaskTimelineText>
                  <div>
                    Next scheduled appointment:{" "}
                    {formatWorkflowsDate(task.futureScheduledContacts[0])}
                  </div>
                </TaskTimelineText>
              </>
            )}

          <TaskTimelineDonut />
          <TaskTimelineText>
            <TaskTimelineDueDate overdue={task.isOverdue}>
              {task.dueDateDisplayShort}
            </TaskTimelineDueDate>
          </TaskTimelineText>
        </TaskTimelineGroup>

        <SnoozeTaskDropdown
          task={task}
          taskConfig={
            task.person.supervisionTasks?.tasksConfig?.tasks[task.type]
          }
          onSelectSnoozeDays={(days) => {
            if (reasonEnabled) {
              setPendingSnoozeDays(days);
            } else {
              persistSnooze(days, undefined);
            }
          }}
        />
      </TaskItemV2>
      <SnoozedTaskInfo task={task} />
    </TaskItemWrapper>
  );
};

export const PreviewTasks = observer(function PreviewTasks({
  person,
  showSnoozeDropdown,
  empty,
  hideSnoozed,
}: PersonProfileProps & {
  showSnoozeDropdown: boolean;
  empty?: React.ReactNode;
  hideSnoozed?: boolean;
}) {
  const sourceTasks =
    person.supervisionTasks?.[
      hideSnoozed ? "readyOrderedTasks" : "orderedTasks"
    ] ?? [];
  const tasks = uniqBy(sourceTasks, "type");
  const needs = person.supervisionTasks?.needs ?? [];

  if (!tasks.length && !needs.length && empty) {
    return empty;
  } else if (!tasks.length && !needs.length) {
    return null;
  }

  return (
    <TasksWrapper>
      <TaskItems>
        {tasks.map((task) => {
          return <TaskPreviewV2 task={task} key={task.key} />;
        })}
        {needs.map((need) => {
          return (
            <TaskItem key={need.type}>
              <TaskName>{NEED_DISPLAY_NAME[need.type]}</TaskName>
            </TaskItem>
          );
        })}
      </TaskItems>
    </TasksWrapper>
  );
});
